import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import useCategorias from '../hooks/useCategorias';
import useIncidenciasMapa from '../hooks/useIncidenciasMapa';
import useConfiguracion from '../hooks/useConfiguracion';
import { DISTRITOS, DISTRITO_DEFECTO, obtenerDistrito } from '../utils/distritos';
import useArchivos from '../hooks/useArchivos';
import TopSearchBar from '../components/Shell/TopSearchBar';
import ModalSeleccion from '../components/Shell/ModalSeleccion';
import ComentariosPunto from '../components/Shell/ComentariosPunto';
import MapFloatingControls from '../components/Shell/MapFloatingControls';
import BottomNav from '../components/Shell/BottomNav';
import ReportWizard from '../components/Reportar/ReportWizard';
import SeguimientoTicket from '../components/Reportar/SeguimientoTicket';
import CampaignDock from '../components/campaign/CampaignDock';
import CampaignStory from '../components/campaign/CampaignStory';
import '../styles/MapaPrincipal.css';

const ZOOM_DEFECTO = 15;
const ZOOM_BUSQUEDA = 16;

// Distritos de Lima Metropolitana. El primero es el que se muestra por
// defecto. La geometría/centro de cada uno se resuelve contra Nominatim al
// seleccionarlo (no hace falta hardcodear coordenadas).

// Paleta llamativa para colorear pines e íconos por categoría (HU-022).
const PALETA_CATEGORIAS = [
  '#E53935', '#FB8C00', '#43A047', '#1E88E5', '#8E24AA',
  '#00897B', '#F4511E', '#3949AB', '#C0CA33', '#D81B60',
];

const crearIconoPin = (color) => new L.DivIcon({
  className: '',
  html: `<div class="mp-pin" style="background:${color || 'var(--brand)'}"></div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 30],
});

// Etiqueta con el nombre del distrito activo, ANCLADA en su centro (queda
// "pegada" al lugar al mover/zoom el mapa). No interactiva.
const crearEtiquetaDistrito = (nombre) => new L.DivIcon({
  className: 'mp-etiqueta-distrito',
  html: `<span>${nombre}</span>`,
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

// Nombre legible del estado (el mapa devuelve el código en `estado`).
const ETIQUETA_ESTADO = {
  pendiente: 'Pendiente',
  recepcionado: 'Recepcionado',
  en_progreso: 'En progreso',
  culminado: 'Culminado',
  anulado: 'Anulado',
};

// Fecha/hora de publicación en la zona horaria configurada (default Lima).
const formatearFechaHora = (iso, zona) => {
  try {
    return new Date(iso).toLocaleString('es-PE', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: zona || 'America/Lima',
    });
  } catch {
    return iso;
  }
};

const iconoPin = crearIconoPin();

const iconoUsuario = new L.DivIcon({
  className: '',
  html: '<div class="mp-user-dot"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

/**
 * Pantalla base de la app del ciudadano: mapa a pantalla completa (README
 * diseño sección 5.1). Todo lo demás (formulario de reporte, seguimiento)
 * se abre encima de esto, nunca lo reemplaza — "el mapa es la aplicación".
 */

// Tipos de vía que Nominatim devuelve y que sí queremos priorizar en la
// lista de sugerencias (calles, avenidas, pasajes, plazas...). El resto
// (ciudades, regiones, países) se muestra igual pero al final.
const TIPOS_CALLE = new Set([
  'street', 'road', 'avenue', 'avenue_road', 'boulevard', 'drive',
  'lane', 'way', 'path', 'footway', 'pedestrian', 'steps',
  'service', 'track', 'residential', 'living_street', 'terrace',
  'place', 'square', 'alley', 'passage', 'mews', 'crescent',
  'close', 'court', 'gate', 'grove', 'hill', 'mount', 'parade',
  'promenade', 'rise', 'row', 'vale', 'view', 'walk',
]);

const ETIQUETA_TIPO = {
  street: 'Calle', road: 'Calle', avenue: 'Av.', avenue_road: 'Av.',
  boulevard: 'Blvd.', drive: 'Av.', lane: 'Pasaje', way: 'Pasaje',
  path: 'Sendero', footway: 'Peatonal', pedestrian: 'Peatonal',
  steps: 'Escaleras', service: 'Servicio', track: 'Pista',
  residential: 'Residencial', living_street: 'Zona 30', terrace: 'Terraza',
  place: 'Plaza', square: 'Plaza', alley: 'Callejón', passage: 'Pasaje',
  mews: 'Pasaje', crescent: 'Media luna', close: 'Callejón',
  court: 'Callejón', gate: 'Entrada', grove: 'Arboleda', hill: 'Cerro',
  mount: 'Cerro', parade: 'Paseo', promenade: 'Paseo', rise: 'Subida',
  row: 'Fila', vale: 'Valle', view: 'Vista', walk: 'Paseo',
};

// Radio (~39 km) alrededor del centro configurado de la instancia, usado
// mientras todavía no se resolvió la geometría del distrito elegido.
const DELTA_VIEWBOX = 0.35;

const construirViewboxCentro = (centro) => {
  const lat = Number(centro.lat);
  const lng = Number(centro.lng);
  return `${lng - DELTA_VIEWBOX},${lat + DELTA_VIEWBOX},${lng + DELTA_VIEWBOX},${lat - DELTA_VIEWBOX}`;
};

// boundingbox de Nominatim = [sur, norte, oeste, este] → viewbox = oeste,norte,este,sur
const viewboxDesdeBbox = (bbox) => {
  const [sur, norte, oeste, este] = bbox.map(Number);
  return `${oeste},${norte},${este},${sur}`;
};

// Convierte un geojson (Polygon o MultiPolygon) en anillos [lat,lng] para
// dibujar el contorno del distrito en el mapa.
const geojsonARings = (geojson) => {
  if (!geojson) return [];
  const aRing = (anillo) => anillo.map(([lng, lat]) => [lat, lng]);
  if (geojson.type === 'Polygon') return [aRing(geojson.coordinates[0])];
  if (geojson.type === 'MultiPolygon') return geojson.coordinates.map((p) => aRing(p[0]));
  return [];
};

// Devuelve hasta 10 resultados de Nominatim acotados a Perú y al viewbox de
// la zona activa (distrito), con las calles/avenidas primero y una etiqueta
// legible por tipo ("Calle", "Av.", "Pasaje"...).
const buscarDirecciones = async (texto, viewbox) => {
  const params = new URLSearchParams({
    q: texto,
    format: 'json',
    limit: '15',
    addressdetails: '1',
    'accept-language': 'es',
    countrycodes: 'pe',
    viewbox,
    bounded: '1',
  });
  const respuesta = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
  const datos = await respuesta.json();
  return datos
    .map((r) => {
      const a = r.address || {};
      const tipo = r.type || (a.road ? 'street' : r.type);
      const titulo = r.name || a.road || a.pedestrian || a.footway || a.square || r.display_name || '';
      const zona = [a.neighbourhood, a.suburb]
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i && v !== titulo)
        .join(', ');
      return {
        ...r,
        _tipo: tipo,
        _label: ETIQUETA_TIPO[tipo] || (a.road ? 'Calle' : 'Lugar'),
        _titulo: titulo,
        _subtitulo: zona,
      };
    })
    .sort((a, b) => {
      const aCalle = TIPOS_CALLE.has(a._tipo);
      const bCalle = TIPOS_CALLE.has(b._tipo);
      if (aCalle && !bCalle) return -1;
      if (!aCalle && bCalle) return 1;
      return 0;
    })
    .slice(0, 10);
};

/**
 * Controla la vista del mapa sin remontarlo: `vista` puede ser
 * `{ tipo: 'bounds', bbox }` (encuadra el distrito) o
 * `{ tipo: 'center', lat, lng, zoom }` (centra en un resultado/GPS).
 */
const MapController = ({ vista }) => {
  const map = useMap();
  useEffect(() => {
    if (!vista) return;
    if (vista.tipo === 'bounds') {
      const [sur, norte, oeste, este] = vista.bbox.map(Number);
      map.fitBounds([[sur, oeste], [norte, este]], { padding: [40, 50], maxZoom: 15 });
    } else if (vista.tipo === 'center') {
      map.setView([vista.lat, vista.lng], vista.zoom ?? ZOOM_BUSQUEDA, { animate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, map]);
  return null;
};

/**
 * Encabezado configurable sobre el buscador (HU-017), con su propio estado de
 * rotación de mensajes (HU-028). Se separó de `MapaPrincipal` a propósito: si
 * el temporizador viviera en el padre, cada 4 s re-renderizaría TODA la
 * pantalla — incluidos los `<Popup>` de Leaflet — y eso hacía parpadear el mapa
 * y cortaba la escritura en los comentarios del pin.
 */
const EncabezadoMapa = ({ configuracion, mensajes }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (mensajes.length <= 1) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % mensajes.length), 4000);
    return () => clearInterval(id);
  }, [mensajes.length]);

  return (
    <div
      className="mp-encabezado"
      style={{
        background: configuracion.encabezado_gradiente
          ? `linear-gradient(120deg, ${configuracion.encabezado_color_fondo || '#FFFFFF'}, ${configuracion.encabezado_color_fondo_2 || '#FFFFFF'})`
          : (configuracion.encabezado_color_fondo || '#FFFFFF'),
      }}
    >
      {configuracion.encabezado_imagen && (
        <img
          className="mp-encabezado-img"
          src={configuracion.encabezado_imagen}
          alt={configuracion.encabezado_titulo || configuracion.nombre_entidad || ''}
        />
      )}
      {!configuracion.encabezado_imagen && mensajes.length > 0 && (
        <div className="mp-encabezado-texto">
          <div className="mp-encabezado-slides">
            {mensajes.map((m, i) => (
              <strong
                key={i}
                className={`mp-encabezado-slide${i === idx ? ' mp-encabezado-slide-activo' : ''}`}
                style={{ color: configuracion.encabezado_color_texto || '#14161A' }}
              >
                {m}
              </strong>
            ))}
          </div>
        </div>
      )}

      {!configuracion.encabezado_imagen && mensajes.length === 0 && (configuracion.encabezado_titulo || configuracion.encabezado_subtitulo) && (
        <div className="mp-encabezado-texto">
          <div className="mp-encabezado-pista">
            {[0, 1].map((copia) => (
              <span className="mp-encabezado-bloque" key={copia} aria-hidden={copia === 1}>
                {configuracion.encabezado_titulo && (
                  <strong style={{ color: configuracion.encabezado_color_texto || '#14161A' }}>
                    {configuracion.encabezado_titulo}
                  </strong>
                )}
                {configuracion.encabezado_subtitulo && (
                  <span style={{ color: configuracion.encabezado_color_subtitulo || '#14161A' }}>
                    {configuracion.encabezado_subtitulo}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MapaPrincipal = () => {
  const navigate = useNavigate();
  const { categorias } = useCategorias();
  const [categoriaActivaId, setCategoriaActivaId] = useState(null);
  const { pines } = useIncidenciasMapa(categoriaActivaId);
  const { configuracion } = useConfiguracion();

  const [posicionUsuario, setPosicionUsuario] = useState(null);
  const [locating, setLocating] = useState(false);
  const [vista, setVista] = useState(null);
  const [wizardAbierto, setWizardAbierto] = useState(false);
  const [seguimientoAbierto, setSeguimientoAbierto] = useState(false);
  const [codigoParaSeguimiento, setCodigoParaSeguimiento] = useState('');
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const [centroBusqueda, setCentroBusqueda] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const debounceRef = useRef(null);
  const [subidaEvidencia, setSubidaEvidencia] = useState(null); // {total, completadas} | null
  const { subirArchivo } = useArchivos();

  // Distrito activo + su geometría (polígono para resaltar, bbox para acotar
  // la búsqueda). Se resuelve contra Nominatim al montar y al cambiar.
  const [distrito, setDistrito] = useState(configuracion.distrito_defecto || DISTRITO_DEFECTO);
  // Aplica el distrito por defecto configurado una sola vez (al llegar la
  // configuración), sin pisar una selección manual posterior del ciudadano.
  const distritoInicialRef = useRef(false);
  useEffect(() => {
    if (distritoInicialRef.current) return;
    const def = configuracion.distrito_defecto;
    if (def) {
      distritoInicialRef.current = true;
      setDistrito(def);
    }
  }, [configuracion.distrito_defecto]);
  const [distritoGeo, setDistritoGeo] = useState(null);
  // Centro (lat/lng) del distrito + opciones del toolbox (qué capas se ven).
  const [centroDistrito, setCentroDistrito] = useState(null);
  const [toolsAbierto, setToolsAbierto] = useState(false);
  const [opciones, setOpciones] = useState({ nombre: true, contorno: true, pines: true });
  // Modal de selección abierto: 'distrito' | 'categoria' | null.
  const [modalFiltro, setModalFiltro] = useState(null);
  // Pin seleccionado: abre el modal centrado con la info y los comentarios, y
  // oculta el encabezado/buscador; al cerrarlo, vuelven a aparecer.
  const [pinSeleccionado, setPinSeleccionado] = useState(null);

  const centroInicial = useMemo(
    () => ({ lat: Number(configuracion.mapa_lat), lng: Number(configuracion.mapa_lng) }),
    [configuracion]
  );

  // Al montar (y al cambiar de distrito) resuelve la geometría, resalta el
  // contorno y encuadra la vista en el distrito — así el ciudadano identifica
  // dónde está desde el arranque (por defecto: Jesús María).
  useEffect(() => {
    let cancelado = false;
    setDistritoGeo(null);
    (async () => {
      try {
        const info = await obtenerDistrito(distrito);
        if (cancelado || !info) return;
        setDistritoGeo({ geojson: info.geojson, bbox: info.boundingbox });
        if (info.lat && info.lon) {
          setCentroDistrito({ lat: Number(info.lat), lng: Number(info.lon) });
        }
        setVista({ tipo: 'bounds', bbox: info.boundingbox });
      } catch {
        // Sin geometría: la búsqueda sigue usando el centro configurado.
      }
    })();
    return () => { cancelado = true; };
  }, [distrito]);

  const viewboxActivo = distritoGeo?.bbox
    ? viewboxDesdeBbox(distritoGeo.bbox)
    : construirViewboxCentro(centroInicial);

  const handleLocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCentroBusqueda(null);
        setPosicionUsuario({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setVista({ tipo: 'center', lat: position.coords.latitude, lng: position.coords.longitude, zoom: ZOOM_BUSQUEDA });
        setLocating(false);
      },
      () => setLocating(false)
    );
  };

  // Geocodifica contra Nominatim (mismo proveedor de los tiles, sin
  // dependencia nueva). Mientras el ciudadano escribe, se piden hasta 10
  // coincidencias (acotadas al distrito) y se muestran como lista debajo de
  // la barra; al elegir una (o Enter) el mapa se centra en ella.
  const centrarEnResultado = (resultado) => {
    setTextoBusqueda(resultado._titulo || resultado.display_name || textoBusqueda);
    const lat = parseFloat(resultado.lat);
    const lng = parseFloat(resultado.lon);
    setCentroBusqueda({ lat, lng });
    setSugerencias([]);
    setErrorBusqueda('');
    setVista({ tipo: 'center', lat, lng, zoom: ZOOM_BUSQUEDA });
  };

  const seleccionarSugerencia = (resultado) => centrarEnResultado(resultado);

  const manejarBusqueda = (texto, opciones = {}) => {
    setTextoBusqueda(texto);
    setErrorBusqueda('');

    if (!texto?.trim()) {
      setSugerencias([]);
      return;
    }

    // Mientras escribe: búsqueda debounced que alimenta la lista de sugerencias.
    if (opciones.soloEscribir) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setBuscando(true);
        try {
          const resultados = await buscarDirecciones(texto, viewboxActivo);
          setSugerencias(resultados);
        } catch {
          setSugerencias([]);
        } finally {
          setBuscando(false);
        }
      }, 400);
      return;
    }

    // Enter o botón: centrar el mapa en la sugerencia elegida o en la primera.
    (async () => {
      setBuscando(true);
      try {
        const resultados = sugerencias.length > 0 ? sugerencias : await buscarDirecciones(texto, viewboxActivo);
        if (resultados[0]) {
          centrarEnResultado(resultados[0]);
        } else {
          setErrorBusqueda('No encontramos esa dirección en el distrito. Probá con otra búsqueda.');
        }
      } catch {
        setErrorBusqueda('Sin conexión para buscar — probá de nuevo en un momento.');
      } finally {
        setBuscando(false);
      }
    })();
  };

  const irAMapa = () => {
    setWizardAbierto(false);
    setSeguimientoAbierto(false);
  };

  const irAReportar = () => setWizardAbierto(true);

  const irASeguimiento = (codigo = '') => {
    setCodigoParaSeguimiento(codigo);
    setWizardAbierto(false);
    setSeguimientoAbierto(true);
  };

  // Sube la evidencia después de que el ticket ya se mostró al ciudadano
  // (HU-008 Fase 7) — antes `ReportWizard` esperaba que TODO terminara de
  // subir antes de dejar ver el ticket, y con datos móviles eso se sentía
  // "colgado". Vive acá (no en ReportWizard) para que la subida siga aunque
  // el ciudadano cierre el wizard y vuelva al mapa — este componente no se
  // desmonta mientras la app siga abierta.
  const subirEvidenciaEnSegundoPlano = (evidencias, incidenciaId, lat, lng, direccion, climaTexto) => {
    if (evidencias.length === 0) return;
    setSubidaEvidencia({ total: evidencias.length, completadas: 0 });
    Promise.all(evidencias.map(async (ev) => {
      const subida = await subirArchivo(ev.blob, incidenciaId, lat, lng, direccion, climaTexto);
      if (!subida.success) console.warn('No se pudo subir un adjunto:', ev.nombre, subida.error);
      setSubidaEvidencia((s) => (s ? { ...s, completadas: s.completadas + 1 } : s));
    })).then(() => {
      setTimeout(() => setSubidaEvidencia(null), 1500);
    });
  };

  const ringsDistrito = useMemo(() => geojsonARings(distritoGeo?.geojson), [distritoGeo]);

  // HU-017: encabezado configurable sobre el buscador. Se muestra solo si
  // está activo Y hay algo que mostrar (imagen y/o texto).
  const encabezadoActivo = !!configuracion.encabezado_activo
    && !!(configuracion.encabezado_imagen || configuracion.encabezado_titulo || configuracion.encabezado_subtitulo);

  const categoriaActivaNombre = categorias.find((c) => c.cat_inId === categoriaActivaId)?.cat_chNombre;

  // Mensajes del encabezado (HU-028): uno por línea, se rotan tipo PowerPoint.
  const mensajesEncabezado = (configuracion.encabezado_mensajes || '')
    .split('\n').map((m) => m.trim()).filter(Boolean);

  // Un ícono de pin por categoría (color de la paleta), para el mapa (HU-022).
  const iconosCategoria = useMemo(() => {
    const mapa = new Map();
    categorias.forEach((c, idx) => {
      mapa.set(c.cat_chNombre, crearIconoPin(PALETA_CATEGORIAS[idx % PALETA_CATEGORIAS.length]));
    });
    return mapa;
  }, [categorias]);

  return (
    <div className="mapa-principal">
      <MapContainer
        center={[centroInicial.lat, centroInicial.lng]}
        zoom={ZOOM_DEFECTO}
        zoomControl={false}
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapController vista={vista} />

        {/* Contorno del distrito activo — para que el ciudadano identifique
            en qué zona está buscando. */}
        {opciones.contorno && ringsDistrito.map((ring, i) => (
          <Polygon
            key={`distrito-${i}`}
            positions={ring}
            pathOptions={{ color: '#1a73e8', weight: 2, fillColor: '#1a73e8', fillOpacity: 0.08 }}
          />
        ))}

        {/* Nombre del distrito anclado en su ubicación (controlado por el toolbox). */}
        {opciones.nombre && centroDistrito && (
          <Marker
            position={[centroDistrito.lat, centroDistrito.lng]}
            icon={crearEtiquetaDistrito(distrito)}
            interactive={false}
          />
        )}

        <MapFloatingControls onLocate={handleLocate} locating={locating} />

        {posicionUsuario && (
          <Marker position={posicionUsuario} icon={iconoUsuario} />
        )}

        {centroBusqueda && (
          <Marker position={centroBusqueda} icon={iconoPin}>
            <Popup className="mp-pin-popup">{textoBusqueda}</Popup>
          </Marker>
        )}

        {/* Clustering de marcadores (README diseño, sección 8) — con muchas
            incidencias en la misma zona, mostrar cada pin suelto satura el
            mapa; agrupados en un número, se abren al hacer zoom o clic. */}
        {opciones.pines && (
          <MarkerClusterGroup chunkedLoading maxClusterRadius={50}>
            {pines
              .filter((p) => p.lat != null && p.lng != null)
              .map((pin) => (
                <Marker
                  key={pin.inc_inId}
                  position={[pin.lat, pin.lng]}
                  icon={iconosCategoria.get(pin.categoria) || iconoPin}
                  eventHandlers={{ click: () => setPinSeleccionado(pin) }}
                />
              ))}
          </MarkerClusterGroup>
        )}
      </MapContainer>

      <div className="mp-capa-superior">
        {!pinSeleccionado && encabezadoActivo && (
          <EncabezadoMapa configuracion={configuracion} mensajes={mensajesEncabezado} />
        )}

        {!pinSeleccionado && (
          <>
            <TopSearchBar
              nombreEntidad={configuracion.nombre_entidad}
              logo={configuracion.logo}
              valor={textoBusqueda}
              onBuscar={manejarBusqueda}
              buscando={buscando}
              sugerencias={sugerencias}
              onSeleccionar={seleccionarSugerencia}
            />
            {errorBusqueda && <div className="mp-busqueda-error">{errorBusqueda}</div>}
          </>
        )}

        <div className="mp-filtros-botones">
          <button
            type="button"
            className={`mp-filtro-btn${modalFiltro === 'distrito' ? ' activo' : ''}`}
            onClick={() => setModalFiltro('distrito')}
          >
            <i className="fas fa-location-dot"></i> Distrito: {distrito}
          </button>
          <button
            type="button"
            className={`mp-filtro-btn${modalFiltro === 'categoria' ? ' activo' : ''}`}
            onClick={() => setModalFiltro('categoria')}
          >
            <i className="fas fa-filter"></i> {categoriaActivaNombre ? `Filtrar · ${categoriaActivaNombre}` : 'Filtrar'}
          </button>
        </div>
      </div>

      {/* Toolbox del mapa: muestra/oculta nombre del distrito, contorno y pines. */}
      <div className={`mp-toolbox${toolsAbierto ? ' mp-toolbox-abierto' : ''}`}>
        {toolsAbierto && (
          <div className="mp-toolbox-panel">
            <span className="mp-toolbox-titulo">Opciones del mapa</span>
            <label className="mp-toolbox-opcion">
              <input type="checkbox" checked={opciones.nombre}
                onChange={(e) => setOpciones({ ...opciones, nombre: e.target.checked })} />
              Nombre del distrito
            </label>
            <label className="mp-toolbox-opcion">
              <input type="checkbox" checked={opciones.contorno}
                onChange={(e) => setOpciones({ ...opciones, contorno: e.target.checked })} />
              Contorno del distrito
            </label>
            <label className="mp-toolbox-opcion">
              <input type="checkbox" checked={opciones.pines}
                onChange={(e) => setOpciones({ ...opciones, pines: e.target.checked })} />
              Registros (pines)
            </label>
          </div>
        )}
        <button
          type="button"
          className="mp-toolbox-btn"
          onClick={() => setToolsAbierto((v) => !v)}
          title="Opciones del mapa"
          aria-label="Opciones del mapa"
        >
          {/* Ícono SVG propio (no depende de fuentes externas, siempre visible). */}
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {toolsAbierto ? (
              <path d="M6 6l12 12M18 6L6 18" />
            ) : (
              <>
                <line x1="4" y1="8" x2="20" y2="8" />
                <circle cx="9" cy="8" r="2.6" />
                <line x1="4" y1="16" x2="20" y2="16" />
                <circle cx="15" cy="16" r="2.6" />
              </>
            )}
          </svg>
        </button>
      </div>

      {pinSeleccionado && (
        <div className="mp-pin-modal-overlay" onClick={() => setPinSeleccionado(null)}>
          <div className="mp-pin-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="mp-pin-modal-cerrar"
              onClick={() => setPinSeleccionado(null)}
              aria-label="Cerrar"
            >
              ✕
            </button>
            <strong className="mp-pin-modal-categoria">{pinSeleccionado.categoria || 'Sin categoría'}</strong>
            <div className="mp-pin-modal-estado">{ETIQUETA_ESTADO[pinSeleccionado.estado] || pinSeleccionado.estado}</div>
            {pinSeleccionado.fecha_creacion && (
              <div className="mp-pin-fecha">{formatearFechaHora(pinSeleccionado.fecha_creacion, configuracion.zona_horaria)}</div>
            )}
            <ComentariosPunto incidenciaId={pinSeleccionado.inc_inId} configuracion={configuracion} />
          </div>
        </div>
      )}

      {modalFiltro === 'distrito' && (
        <ModalSeleccion
          titulo="Elige tu distrito"
          placeholder="Buscar distrito…"
          opciones={DISTRITOS.map((d) => ({ id: d, label: d }))}
          activoId={distrito}
          onSeleccionar={(d) => { setDistrito(d); setModalFiltro(null); }}
          onCerrar={() => setModalFiltro(null)}
        />
      )}
      {modalFiltro === 'categoria' && (
        <ModalSeleccion
          titulo="Incidencia o Propuesta"
          placeholder="Buscar categoría…"
          opciones={[
            { id: null, label: 'Todas' },
            ...categorias.map((c, idx) => ({
              id: c.cat_inId,
              label: c.cat_chNombre,
              color: PALETA_CATEGORIAS[idx % PALETA_CATEGORIAS.length],
            })),
          ]}
          activoId={categoriaActivaId}
          onSeleccionar={(id) => { setCategoriaActivaId(id); setModalFiltro(null); }}
          onCerrar={() => setModalFiltro(null)}
        />
      )}
      <BottomNav
        activo={seguimientoAbierto ? 'seguimiento' : 'mapa'}
        onMapa={irAMapa}
        onReportar={irAReportar}
        onSeguimiento={() => irASeguimiento()}
        onInicio={() => navigate('/splash')}
      />

      <CampaignDock configuracion={configuracion} />
      <CampaignStory configuracion={configuracion} />

      {wizardAbierto && (
        <ReportWizard
          onCerrar={() => setWizardAbierto(false)}
          onVerTicket={irASeguimiento}
          onEnviarEvidenciaEnSegundoPlano={subirEvidenciaEnSegundoPlano}
        />
      )}
      {seguimientoAbierto && (
        <SeguimientoTicket
          codigoInicial={codigoParaSeguimiento}
          onCerrar={() => setSeguimientoAbierto(false)}
        />
      )}

      {subidaEvidencia && (
        <div className="mp-subida-evidencia">
          <i className="fas fa-cloud-arrow-up"></i>
          Subiendo evidencia — {subidaEvidencia.completadas}/{subidaEvidencia.total}
        </div>
      )}
    </div>
  );
};

export default MapaPrincipal;
